import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import CrossCircleXs from '@lichtblick/icons/svg/cross-circle/cross-circle-xs';
import { SearchIconXs } from '@lichtblick/icons/svg/search/search-xs';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { doesValueMatchSearch } from '../ComboBoxInput/ComboBoxInput.helpers';
import { FieldFooter } from '../FieldFooter/FieldFooter';
import { Label } from '../Label/Label';
import { IconWrapper, Input, InputRow, LabelWrapper, Slot, Wrapper } from './FilterSelect.styles';
export const FilterSelect = /*#__PURE__*/ forwardRef(({ className, 'data-testid': testId, icon, id, isDisabled, label, name, onChange, options, statusLabel, statusType, value, ...props }, ref)=>{
    const [searchValue, setSearchValue] = useState('');
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);
    const inputId = id || name;
    const labelId = inputId && `${inputId}_label`;
    const statusId = inputId && `${inputId}_validationMessage`;
    const filteredOptions = options.filter(({ keywords, label })=>doesValueMatchSearch(label, searchValue) || keywords && doesValueMatchSearch(keywords.join(), searchValue));
    const updateSearchValue = useCallback((searchValue)=>setSearchValue(options.find((option)=>option.value === searchValue)?.label ?? ''), [
        options
    ]);
    useEffect(()=>updateSearchValue(value), [
        updateSearchValue,
        value
    ]);
    const setValue = ({ target })=>{
        const exactOption = options.find(({ label })=>label === target.value);
        dropdownRef.current?.open();
        setSearchValue(target.value);
        if (exactOption) {
            dispatchChange(exactOption.value);
        } else if (!target.value) {
            dispatchChange('');
        }
    };
    const focusInput = ()=>inputRef.current?.focus();
    const dispatchChange = (value)=>{
        onChange?.({
            target: {
                value,
                name
            }
        });
        updateSearchValue(value); // needed in case current value was selected again after search value was altered
        dropdownRef.current?.close();
    };
    const openDropdown = ()=>{
        focusInput();
        dropdownRef.current?.open();
    };
    const clearInput = ()=>{
        dispatchChange('');
        setSearchValue('');
        focusInput();
        dropdownRef.current?.close();
    };
    return /*#__PURE__*/ _jsxs(Wrapper, {
        className: className,
        children: [
            /*#__PURE__*/ _jsx(LabelWrapper, {
                children: Boolean(label) && /*#__PURE__*/ _jsx(Label, {
                    htmlFor: inputId,
                    id: labelId,
                    isBold: true,
                    children: label
                })
            }),
            /*#__PURE__*/ _jsxs(InputRow, {
                $isDisabled: isDisabled,
                $statusType: statusType,
                onClose: focusInput,
                onOptionSelect: dispatchChange,
                options: filteredOptions,
                ref: dropdownRef,
                children: [
                    /*#__PURE__*/ _jsx("input", {
                        name: name,
                        type: "hidden",
                        value: value
                    }),
                    /*#__PURE__*/ _jsx(Input, {
                        ...props,
                        "aria-describedby": statusId,
                        "aria-invalid": statusType === 'error',
                        "aria-labelledby": labelId,
                        "data-testid": testId || inputId,
                        disabled: isDisabled,
                        id: inputId,
                        onChange: setValue,
                        ref: (instance)=>{
                            if (typeof ref === 'function') {
                                ref(instance);
                            } else if (ref) {
                                ref.current = instance;
                            }
                            inputRef.current = instance;
                        },
                        value: searchValue
                    }),
                    !isDisabled && /*#__PURE__*/ _jsxs(Slot, {
                        children: [
                            Boolean(searchValue) && /*#__PURE__*/ _jsx(IconWrapper, {
                                onClick: clearInput,
                                children: /*#__PURE__*/ _jsx(CrossCircleXs, {})
                            }),
                            /*#__PURE__*/ _jsx(IconWrapper, {
                                onClick: openDropdown,
                                children: /*#__PURE__*/ _jsx(SearchIconXs, {})
                            })
                        ]
                    })
                ]
            }),
            Boolean(statusLabel) && /*#__PURE__*/ _jsx(FieldFooter, {
                fieldFooterLabel: statusLabel,
                fieldFooterType: statusType,
                icon: icon,
                id: statusId
            })
        ]
    });
});

//# sourceMappingURL=FilterSelect.js.map